import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Input, message as antMessage, Spin } from "antd";
import logo from "../../assets/logo.png";
import { getCurrentLanguage } from "@/store/data/global";
import {
  FacebookOutlined,
  YoutubeOutlined,
  InstagramOutlined,
  ArrowRightOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import "./index.less";

const { TextArea } = Input;

const BottomPage = () => {
  const location = useLocation();
  const currentLanguage = useSelector(getCurrentLanguage);
  const [formData, setFormData] = useState({
    email: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const footerRef = useRef(null);

  // 监听 hash 变化，处理跳转
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#footer') {
        setTimeout(() => {
          scrollToFooter();
        }, 100);
      }
    };

    if (window.location.hash === '#footer') {
      handleHashChange();
    }

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const scrollToFooter = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      const html = document.documentElement;
      const body = document.body;
      
      const viewportHeight = window.innerHeight;
      const documentHeight = Math.max(
        body.scrollHeight, body.offsetHeight,
        html.clientHeight, html.scrollHeight, html.offsetHeight
      );
      
      if (documentHeight <= viewportHeight) {
        body.style.minHeight = `${viewportHeight + 100}px`;
      }

      footerElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });

      setTimeout(() => {
        body.style.minHeight = '';
      }, 1000);
    }
  };

  useEffect(() => {
    window.scrollToFooter = scrollToFooter;
    
    return () => {
      delete window.scrollToFooter;
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formData.email.trim()) {
      antMessage.error(
        currentLanguage === "EN" 
          ? "Please enter your email" 
          : "メールアドレスを入力してください"
      );
      return false;
    }

    if (!emailRegex.test(formData.email)) {
      antMessage.error(
        currentLanguage === "EN" 
          ? "Please enter a valid email address" 
          : "有効なメールアドレスを入力してください"
      );
      return false;
    }

    if (!formData.message.trim()) {
      antMessage.error(
        currentLanguage === "EN" 
          ? "Please enter your message" 
          : "メッセージを入力してください"
      );
      return false;
    }

    if (formData.message.length < 10) {
      antMessage.error(
        currentLanguage === "EN" 
          ? "Message must be at least 10 characters" 
          : "メッセージは10文字以上で入力してください"
      );
      return false;
    }

    return true;
  };

const handleSubmit = async () => {
  if (!validateForm()) return;
  
  setLoading(true);
  
  try {
    // 创建 FormData 对象
    const formDataToSend = new FormData();
    formDataToSend.append("email", formData.email);
    formDataToSend.append("message", formData.message);
    // 实际提交数据到后端
    const response = await fetch("/admin/send_message", {
      method: "POST",
      body: formDataToSend,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    antMessage.success(
      currentLanguage === "EN" 
        ? "Message sent successfully! We'll get back to you soon." 
        : "メッセージを送信しました！近日中にご連絡いたします。"
    );
    
    setSubmitted(true);
    setFormData({ email: "", message: "" });
    
    setTimeout(() => setSubmitted(false), 5000);
    
  } catch (error) {
    console.error("Failed to send message:", error);
    antMessage.error(
      currentLanguage === "EN" 
        ? "Failed to send message. Please try again." 
        : "送信に失敗しました。もう一度お試しください。"
    );
  } finally {
    setLoading(false);
  }
};

  const handleKeyPress = (e) => {
    // 支持 Enter 提交（不按Shift）和 Ctrl+Enter 提交
    if ((e.key === 'Enter' && !e.shiftKey) || (e.key === 'Enter' && e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // 其他代码保持不变...
  const navItems = [
    { path: "/", key: "/", name: { EN: "Home", JA: "ホーム" } },
    {
      path: "/sustainable",
      key: "sustainable",
      name: { EN: "Sustainable Development", JA: "持続可能な開発" },
    },
    {
      path: "/cases",
      key: "cases",
      name: { EN: "Case Studies", JA: "事例紹介" },
    },
    { path: "/news", key: "news", name: { EN: "News", JA: "ニュース" } },
    { path: "/future", key: "future", name: { EN: "Future", JA: "未来" } },
    {
      path: "/business",
      key: "business",
      name: { EN: "Business", JA: "ビジネス" },
    },
  ];

  const isActiveLink = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const addressText = {
    EN: "NSK Building, 27-3, T 束 2-chome, Taito-ku, Tokyo, Japan",
    JA: "東京都台束区台束二丁目27番3号NSKビル",
  };
  const humanText = {
    EN: "松元孝次郎",
    JA: "松元孝次郎",
  };
  const accountText = {
    EN: [
      {
        title: "Privacy Policy",
        link: "/privacy-policy",
      },
      {
        title: "Terms of Use",
        link: "/terms",
      },
    ],
    JA: [
      {
        title: "当サイトのご利用にあたって",
        link: "/privacy-policy",
      },
      {
        title: "クッキーの利用について",
        link: "/terms",
      },
    ],
  };
  const imageJump = {
    EN: [
      {
        title: "Empowering New Energy Enterprises",
        img: require("../../assets/Getintouch/bottom-1.webp"),
      },
      {
        title: "Achievement of Carbon Neutrality",
        img: require("../../assets/Getintouch/bottom-2.webp"),
      },
    ],
    JA: [
      {
        title: "新エネルギー企業を支援する",
        img: require("../../assets/Getintouch/bottom-1.webp"),
      },
      {
        title: "カーボンニュートラリティの達成",
        img: require("../../assets/Getintouch/bottom-2.webp"),
      },
    ],
  };

  return (
    <footer className="global-footer" id="footer" ref={footerRef}>
      <div className="themesCube middle">
        <div className="top-link flex">
          <div className="leftLink">
            <div className="logo">
              <Link to="/">
                <img src={logo} alt="Company Logo" />
              </Link>
            </div>
            <div className="intro">
              {currentLanguage === "EN"
                ? "Creating the Future Production Model Through Technology"
                : "テクノロジーで未来の生産を創造する"}
            </div>
            <div className="SocialJump flex">
              <Link to="/">
                <FacebookOutlined />
              </Link>
              <Link to="/">
                <YoutubeOutlined />
              </Link>
              <Link to="/">
                <InstagramOutlined />
              </Link>
            </div>
          </div>
          <div className="rightMessage">
            <div className="note">
              {currentLanguage === "EN"
                ? "Your opinion & question"
                : "ご意見・ご質問"}
            </div>
            <div className="messageCube flex">
              <div className="inputLeft">
                <div className={`email ${focusedField === 'email' ? 'focused' : ''}`}>
                  <Input 
                    placeholder={currentLanguage === "EN" ? "name@mail.com" : "メールアドレス"} 
                    variant="filled"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onFocus={() => handleFocus('email')}
                    onBlur={handleBlur}
                    disabled={loading || submitted}
                  />
                </div>
                <div className={`message ${focusedField === 'message' ? 'focused' : ''}`}>
                  <TextArea
                    maxLength={500}
                    value={formData.message}
                    variant="filled"
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    onKeyPress={handleKeyPress}
                    onFocus={() => handleFocus('message')}
                    onBlur={handleBlur}
                    placeholder={
                      currentLanguage === "EN" 
                        ? "Your Message" 
                        : "メッセージ"
                    }
                    style={{ height: 120, resize: "none" }}
                    disabled={loading || submitted}
                    showCount
                  />
                </div>
              </div>
              <div 
                className={`submit ${loading ? 'loading' : ''} ${submitted ? 'submitted' : ''}`}
                onClick={!loading && !submitted ? handleSubmit : undefined}
              >
                {loading ? (
                  <Spin size="small" />
                ) : submitted ? (
                  <CheckOutlined style={{ color: '#83007f' }} />
                ) : (
                  <ArrowRightOutlined />
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* 其余部分保持不变 */}
        <div className="middle-link flex">
          <div className="image-jump">
            {imageJump[currentLanguage].map((item, index) => (
              <div
                key={index}
                className="image-item"
                style={{ backgroundImage: `url(${item.img})` }}
              >
                <div className="content-overlay">
                  <div className="image-title">{item.title}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="link-list">
            <dl className="item-in">
              {navItems.map((item) => (
                <dd key={item.key}>
                  <Link
                    to={item.path}
                    className={isActiveLink(item.path) ? "active" : ""}
                  >
                    {item.name[currentLanguage]}
                  </Link>
                </dd>
              ))}
            </dl>
          </div>
          <div className="account-list ">
            {accountText[currentLanguage].map((item, index) => (
              <Link className="account-title" key={index} to={item.link}>
                {item.title}
              </Link>
            ))}
          </div>
          <div className="address-list">
            <div className="human line">{humanText[currentLanguage]}</div>
            <div className="email line"></div>
            <div className="address line">{addressText[currentLanguage]}</div>
          </div>
        </div>
        <div className="bottom-stowage flex">
          <div className="stowage">© 2025 kigen All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
};

export default BottomPage;